import React, { useState, useEffect } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import imageCompression from 'browser-image-compression';
import DashboardNavbar from "../dashboardNavbar"
import useDemoActionGate from '../hooks/useDemoActionGate';
import DemoLockedDialog from '../ui/DemoLockedDialog';

const FormContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
  background: ${({ theme }) => theme.surface};
  border: ${({ theme }) => theme.cardBorder};
  border-radius: ${({ theme }) => theme.rLg};
  padding: 22px;
  box-shadow: ${({ theme }) => theme.cardShadow};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
`;

const Label = styled.label`
  margin-bottom: 6px;
  font-size: ${({ theme }) => theme.fsSm};
  font-weight: 650;
  color: ${({ theme }) => theme.text};
`;

const Input = styled.input`
  padding: 9px 13px;
  margin-bottom: 15px;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: ${({ theme }) => theme.rMd};
  background: ${({ theme }) => theme.surface2};
  color: ${({ theme }) => theme.text};
  font-family: ${({ theme }) => theme.fontBody};
  font-size: 13.5px;
  outline: none;
  transition: .15s;

  &:focus {
    border-color: ${({ theme }) => theme.accent};
    background: ${({ theme }) => theme.surface};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.accentSoft};
  }
`;

const Button = styled.button`
  align-self: flex-start;
  height: 34px;
  padding: 0 18px;
  background: ${({ theme }) => theme.btnBg};
  color: #fff;
  border: none;
  border-radius: ${({ theme }) => theme.rMd};
  cursor: pointer;
  font-family: ${({ theme }) => theme.fontBody};
  font-size: 11.5px;
  font-weight: 650;
  box-shadow: 0 1px 2px rgba(17,20,38,.18), inset 0 1px 0 rgba(255,255,255,.12);
  transition: background-color 0.15s;
  &:hover {
    background: ${({ theme }) => theme.btnBgHover};
  }
`;

const ImageContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
`;

const ImageItem = styled.div`
  padding: 10px;
`;

const Image = styled.img`
  width: 150px;
  height: 150px;
  border-radius: 50%;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
`;

const Organisation = () => {
  const { guardAction, lockedDialogOpen, setLockedDialogOpen } = useDemoActionGate();
  const [name, setName] = useState('StudyAK');
  const [logo, setLogo] = useState('/main.jpeg');
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const menuItems = [ { "title": "Organization", "link": "/Portal/Instructor/organisation" },
    { "title": "Users", "link": "/Portal/Instructor/admin/users" },
    { "title": "Course Catalog", "link": "/Portal/Instructor/ImportCourse" },
    { "title": "Integrations", "link": "/Portal/Instructor/admin/integrations" },
    { "title": "Location & Standard", "link": "/Portal/Instructor/admin/location" }];

  useEffect(() => {
    fetchData();
  }, []);

  const getBasicAuthHeader = () => {
    const username = process.env.REACT_APP_API_USERNAME;
    const password = process.env.REACT_APP_API_PASSWORD;
    const encodedCredentials = btoa(`${username}:${password}`);
    return `Basic ${encodedCredentials}`;
  };

  const fetchData = async () => {
    const userId = localStorage.getItem("userId");
    const apiUrl = process.env.REACT_APP_API_URL;
    try {
      const response = await axios.get(`${apiUrl}/api/organization/${userId}`, {
            headers: {
                'Content-Type': "application/json",
            "Accept": "*/*",
            "Access-Control-Allow-Origin": "*",
            'Authorization': getBasicAuthHeader(),
            },
          });
      if (response.data) {
        setName(response.data.orgName);
        setLogo(`data:image/jpeg;base64,${response.data.orgLogo}`);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        let compressedFile = file;
        const options = {
          maxSizeMB: 0.03, // Set maximum size to 30KB
          maxWidthOrHeight: 300, // Ensure the image dimensions are not too large
          useWebWorker: true
        };

        // Compress the file until it's less than 30KB
        while (compressedFile.size > 30 * 1024) {
          compressedFile = await imageCompression(compressedFile, options);
        }

        const reader = new FileReader();
        reader.onloadend = () => {
          const base64Data = reader.result.split(',')[1];
          setLogo(`data:image/jpeg;base64,${base64Data}`);
        };
        reader.readAsDataURL(compressedFile);
      } catch (error) {
        console.error('Error compressing image:', error);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!guardAction('action.admin.org.save')) return;
    setLoading(true);
    const userId = localStorage.getItem("userId");
    const apiUrl = process.env.REACT_APP_API_URL;
    const logoData = logo.split(',')[1];
    const body = {
      userId,
      orgName: name,
      orgLogo: logoData
    };

    try {
      const response = await axios.post(`${apiUrl}/api/organization`, body, {
        headers: {
            'Content-Type': "application/json",
        "Accept": "*/*",
        "Access-Control-Allow-Origin": "*",
        'Authorization': getBasicAuthHeader(),
        },
      });
      if (response.status === 200) {
        setLogo(`data:image/jpeg;base64,${logoData}`);
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
      window.location.reload();
    }
  };

  const handleEdit = () => {
    if (!guardAction('action.admin.org.save')) return;
    setEditMode(true);
  };

  return (
    <>
    <DashboardNavbar menuItems={menuItems} />
    <FormContainer>
      <Form onSubmit={handleSubmit}>
        <Label htmlFor="text">Organization Name</Label>
        <Input
          type="text"
          id="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          readOnly={!editMode}
        />
        <Label htmlFor="image">Logo</Label>
        <ImageContainer>
          <ImageItem>
            <Image src={logo} alt="Organization Logo" />
          </ImageItem>
        </ImageContainer>
        {editMode && (
          <>
            <Input
              type="file"
              id="image"
              accept="image/*"
              onChange={handleImageChange}
            />
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : 'Save'}
            </Button>
          </>
        )}
      </Form>
      {!editMode && <Button onClick={handleEdit}>Edit</Button>}
    </FormContainer>
    <DemoLockedDialog open={lockedDialogOpen} onClose={() => setLockedDialogOpen(false)} />
    </>
  );
};

export default Organisation;
